import React, { useState, useEffect, useMemo, useId, useRef } from 'react';
import {
  InfoIcon,
  SpinnerIcon,
  BuildingsIcon,
  GlobeIcon,
  WarningCircleIcon,
  UsersThreeIcon,
  TrashIcon
} from '@phosphor-icons/react';
import Modal from './Modal';
import Button from './Button';
import Banner from './Banner';
import Textarea from './Textarea';
import Combobox, { type ComboboxOption } from './Combobox';
import { AllUsersShare, ResourcePermission, ResourcePermissionRole, TenantShare } from 'src/features/chat/types';
import Dropdown from './Dropdown';
import { toast } from 'src/components/ui/toast';
import { apiGet } from '@/lib/api/axios';
import { usePostHog } from 'src/lib/posthog';
import { ConfirmModal } from './ConfirmModal';
import useAuth from 'src/hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { isOrganisationTenantAtPath } from 'src/lib/tenantRouting';
import { MentionWithDisplayName } from 'src/hooks/useMentionUsers';
import { isValidEmail } from 'src/lib/validation/email';
import UserAvatar from 'src/components/ui/UserAvatar';
interface ShareableGroup {
  id: string;
  name: string;
  description: string | null;
}

export interface GroupShareInfo {
  group_id: string;
  group_name: string;
  permission: ResourcePermissionRole;
}

function formatGroupMemberAccess(groupNames: string[]): string | null {
  const names = [...new Set(groupNames.map((n) => n.trim()).filter(Boolean))];
  if (names.length === 0) return null;
  if (names.length === 1) return `to members of ${names[0]}`;
  if (names.length === 2) return `to members of ${names[0]} and ${names[1]}`;
  return `to members of ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function documentLocationHint(inOrganisationFolder: boolean, groupNames: string[]): string | null {
  const groupAccess = formatGroupMemberAccess(groupNames);
  if (inOrganisationFolder && groupAccess) {
    return `This document is already available in the Organisation Folder and ${groupAccess}.`;
  }
  if (inOrganisationFolder) {
    return 'This document is already available in the Organisation Folder.';
  }
  if (groupAccess) {
    return `This document is already available ${groupAccess}.`;
  }
  return null;
}

const ALL_PERMISSION_LABELS: Record<number, string> = {
  [ResourcePermissionRole.READ]: 'View',
  [ResourcePermissionRole.COMMENT]: 'Comment',
  [ResourcePermissionRole.DATA_ENTRY]: 'Data Entry',
  [ResourcePermissionRole.WRITE]: 'Edit',
  [ResourcePermissionRole.SHARE]: 'Share',
  [ResourcePermissionRole.OWNER]: 'Owner'
};

interface OrganizationUser {
  user_id: number;
  email: string;
  firstname?: string | null;
  surname?: string | null;
  tenant_role?: string | null;
}

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  user_email?: string;
  shareType?:
    | 'chat'
    | 'review'
    | 'asset_review'
    | 'obligation'
    | 'write'
    | 'document'
    | 'action'
    | 'dashboard'
    | 'specialist';
  // Current individual shares (unified: includes both direct and inherited-only users)
  // Each share has an optional inherited_permission field (the floor from parent)
  currentShares?: ResourcePermission[];
  // Tenant share info
  tenantShare?: TenantShare | null;
  /** Read-only inherited tenant permission (when no direct tenant share exists on this node) */
  inheritedTenantPermission?: ResourcePermissionRole | null;
  organisationName?: string;
  // Public share info
  publicShare?: AllUsersShare | null;
  // Whether current user is the owner (displayed separately since owners don't have permission records)
  // TODO: In a future migration, users will have their own role with OWNER permission,
  // so this can be checked via permission. For now, ownership is determined by resource creation.
  isOwner?: boolean;
  // Current user's permission level (always displayed at bottom of shares list)
  currentUserPermission?: ResourcePermissionRole;
  // Permissions
  acceptablePermissions?: ResourcePermissionRole[];
  defaultPermission?: ResourcePermissionRole;
  // Callbacks for individual shares
  onAddPeople?: (
    emailList: string[],
    permission: ResourcePermissionRole,
    message: string,
    groupIds?: string[]
  ) => Promise<void>;
  onRemovePerson?: (permissionId: string) => Promise<void>;
  onUpdatePermission?: (permissionId: string, permission: ResourcePermissionRole) => Promise<void>;
  // Callbacks for tenant sharing
  onShareWithOrganisation?: (permission: ResourcePermissionRole) => Promise<void>;
  onUpdateOrganisationPermission?: (permission: ResourcePermissionRole) => Promise<void>;
  onRemoveOrganisationShare?: () => Promise<void>;
  // Callbacks for public sharing
  onShareWithPublic?: (permission: ResourcePermissionRole) => Promise<void>;
  onUpdatePublicPermission?: (permission: ResourcePermissionRole) => Promise<void>;
  onRemovePublicShare?: () => Promise<void>;
  // Separate acceptable permissions for public sharing (e.g., restrict to READ only)
  publicAcceptablePermissions?: ResourcePermissionRole[];
  // Optional restriction message to display (e.g., why public/tenant sharing is disabled)
  shareRestrictionReason?: string;
  /** Max permission level for emails not found in the organisation. When set, external
   *  users are capped at this level and a disclaimer is shown. */
  maxExternalPermission?: ResourcePermissionRole;
  /** Pre-populate the pending users list with these emails when the modal opens */
  initialEmails?: string[];
  /** Called after changes are successfully applied (before onClose).
   *  Receives the list of emails that were actually added/shared. */
  onApplySuccess?: (addedEmails: string[]) => void;
  /** Groups that already have role-level access to this resource */
  currentGroupShares?: GroupShareInfo[];
  /** Called to remove a group's access (all role-level permissions) */
  onRemoveGroupShare?: (groupId: string) => Promise<void>;
  /** When provided, shows a mention-context banner listing people who need access */
  mentionPrompt?: {
    mentionsNeedingAccess: MentionWithDisplayName[];
    /** Hint explaining the recommended permission, e.g. "Comment access is needed to reply" */
    recommendedPermissionHint?: string;
  };
  /** When provided, only users whose email is in this list appear in the search dropdown */
  eligibleEmails?: string[];
  /** When provided, only groups whose ID is in this list appear in the search dropdown */
  eligibleGroupIds?: string[];
  /** Warning shown in the footer when the user has queued a share-with-organisation change */
  organisationShareWarning?: string;
  /** When `shareType` is `document`, include the Move hint only if the caller can relocate the file */
  canMove?: boolean;
  /** Closes Share and opens Move. Required for the clickable Move hint. */
  onMove?: () => void;
  /** Document is already visible in the Organisation Folder */
  inOrganisationFolder?: boolean;
  /** Groups whose members can already open this document (owned or linked) */
  availableInGroupNames?: string[];
}

// Permission dropdown with "No access" option
const PermissionSelect: React.FC<{
  value: ResourcePermissionRole | 'none';
  onChange: (value: ResourcePermissionRole | 'none') => void;
  options: Array<{ label: string; value: ResourcePermissionRole }>;
  showNoAccess?: boolean;
  disabled?: boolean;
  isCurrentUser?: boolean;
}> = ({ value, onChange, options, showNoAccess = true, disabled = false, isCurrentUser = false }) => {
  // Ensure the current value always has a label in the option list even when it falls outside the
  // restricted set (e.g. a group was granted Owner access but the modal only allows READ–WRITE).
  const optionsWithCurrent =
    value !== 'none' && !options.some((o) => o.value === value)
      ? [...options, { label: ALL_PERMISSION_LABELS[value] ?? String(value), value }]
      : options;

  const allOptions = showNoAccess
    ? [{ label: 'No access', value: 'none' as const }, ...optionsWithCurrent]
    : optionsWithCurrent;

  if (isCurrentUser) {
    const label = optionsWithCurrent.find((o) => o.value === value)?.label || 'Full Access';
    return <span className="text-sm text-content-subtle">{label}</span>;
  }

  return (
    <Dropdown
      options={allOptions.map((opt) => ({ label: opt.label, value: String(opt.value) }))}
      initialValue={String(value)}
      onChange={(val) => onChange(val === 'none' ? 'none' : (Number(val) as ResourcePermissionRole))}
      width="w-28"
      size="sm"
      disabled={disabled}
    />
  );
};

// Share item row
interface ShareItemProps {
  name: string;
  subtitle?: string;
  warningMessage?: string;
  avatar?: React.ReactNode;
  permission: ResourcePermissionRole | 'none' | undefined;
  permissionOptions: Array<{ label: string; value: ResourcePermissionRole }>;
  /** When omitted the permission is displayed as read-only text with no dropdown. */
  onPermissionChange?: (permission: ResourcePermissionRole | 'none') => void;
  showNoAccess?: boolean;
  onRemove?: () => void;
  onUndo?: () => void;
  isLoading?: boolean;
  isCurrentUser?: boolean;
}

const ShareItem: React.FC<ShareItemProps> = ({
  name,
  subtitle,
  warningMessage,
  avatar,
  permission,
  permissionOptions,
  onPermissionChange,
  showNoAccess = false,
  onRemove,
  onUndo,
  isLoading = false,
  isCurrentUser = false
}) => {
  const isRemoved = permission === 'none' && onRemove;

  return (
    <div className={`flex items-center justify-between py-1.5 ${isRemoved ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3">
        {avatar}
        <div className="flex flex-col">
          <span className={`text-sm font-medium text-content-default ${isRemoved ? 'line-through' : ''}`}>{name}</span>
          {isRemoved ? (
            <span className="text-xs text-red-500">Access will be removed</span>
          ) : (
            <>
              {subtitle && <span className="text-xs text-content-subtle">{subtitle}</span>}
              {warningMessage && <span className="text-xs text-amber-700">{warningMessage}</span>}
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isLoading ? (
          <SpinnerIcon size={16} className="animate-spin text-content-subtlest" />
        ) : isRemoved && onUndo ? (
          <Button variant="ghost" size="sm" onClick={onUndo}>
            Undo
          </Button>
        ) : !onPermissionChange ? (
          <span className="text-sm text-content-default">
            {permissionOptions.find((o) => o.value === permission)?.label ?? String(permission ?? '')}
          </span>
        ) : (
          <>
            <PermissionSelect
              value={permission ?? 'none'}
              onChange={onPermissionChange}
              options={permissionOptions}
              showNoAccess={showNoAccess}
              isCurrentUser={isCurrentUser}
            />
            {onRemove && !isCurrentUser && (
              <Button variant="ghost" size="sm" icon={TrashIcon} onClick={onRemove} title="Remove access" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Types for pending changes
type PendingChange =
  | { type: 'update'; permissionId: string; newPermission: ResourcePermissionRole }
  | { type: 'remove'; permissionId: string };

type PendingOrgChange =
  | { type: 'share'; permission: ResourcePermissionRole }
  | { type: 'update'; permission: ResourcePermissionRole }
  | { type: 'remove' }
  | null;

type PendingPublicChange =
  | { type: 'share'; permission: ResourcePermissionRole }
  | { type: 'update'; permission: ResourcePermissionRole }
  | { type: 'remove' }
  | null;

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  title = 'Share',
  user_email = '',
  shareType = 'chat',
  currentShares = [],
  tenantShare,
  inheritedTenantPermission,
  organisationName,
  publicShare,
  isOwner = false,
  currentUserPermission,
  acceptablePermissions,
  defaultPermission = ResourcePermissionRole.READ,
  onAddPeople,
  onRemovePerson,
  onUpdatePermission,
  onShareWithOrganisation,
  onUpdateOrganisationPermission,
  onRemoveOrganisationShare,
  onShareWithPublic,
  onUpdatePublicPermission,
  onRemovePublicShare,
  publicAcceptablePermissions,
  shareRestrictionReason,
  maxExternalPermission,
  initialEmails,
  onApplySuccess,
  currentGroupShares = [],
  onRemoveGroupShare,
  mentionPrompt,
  eligibleEmails,
  eligibleGroupIds,
  organisationShareWarning,
  canMove = false,
  onMove,
  inOrganisationFolder = false,
  availableInGroupNames = []
}) => {
  const posthog = usePostHog();
  const searchInputId = useId();
  const messageInputId = useId();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const canAddPeople = Boolean(onAddPeople);
  const individualsOnly = shareType === 'document';
  const locationHint = individualsOnly ? documentLocationHint(inOrganisationFolder, availableInGroupNames) : null;

  // Don't show organisation sharing for personal tenants (only one user, so it's redundant)
  const isOrganisationTenant = isOrganisationTenantAtPath(pathname, user?.tenants);
  // Show if editable (onShareWithOrganisation provided), already shared, or the
  // caller supplied an inherited tenant floor (e.g. organisation-library files).
  const showOrganisationSharing =
    !individualsOnly &&
    isOrganisationTenant &&
    !!organisationName &&
    (!!onShareWithOrganisation || !!tenantShare || inheritedTenantPermission != null);
  const isReadOnlyOrgShare = showOrganisationSharing && !onShareWithOrganisation;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // NEW users pending to be added
  const [pendingNewUsers, setPendingNewUsers] = useState<
    Map<string, { user: OrganizationUser; permission: ResourcePermissionRole }>
  >(new Map());

  // EXISTING share permission changes (permissionId -> change)
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());

  // Organisation permission change
  const [pendingOrgChange, setPendingOrgChange] = useState<PendingOrgChange>(null);

  // Public permission change
  const [pendingPublicChange, setPendingPublicChange] = useState<PendingPublicChange>(null);

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Unsaved changes warning
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const openMoveAfterDiscardRef = useRef(false);

  // Group sharing state
  const [availableGroups, setAvailableGroups] = useState<ShareableGroup[]>([]);
  const [pendingGroupShares, setPendingGroupShares] = useState<
    Map<string, { group: ShareableGroup; permission: ResourcePermissionRole }>
  >(new Map());
  const [pendingGroupRemovals, setPendingGroupRemovals] = useState<Set<string>>(new Set());
  const [pendingGroupPermChanges, setPendingGroupPermChanges] = useState<Map<string, ResourcePermissionRole>>(
    new Map()
  );

  // Count of changes
  const newUserCount = pendingNewUsers.size;
  const updateCount =
    pendingChanges.size +
    (pendingOrgChange ? 1 : 0) +
    (pendingPublicChange ? 1 : 0) +
    pendingGroupRemovals.size +
    pendingGroupPermChanges.size;
  const pendingGroupCount = pendingGroupShares.size;
  const hasAnyChanges = newUserCount > 0 || updateCount > 0 || pendingGroupCount > 0;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setPendingNewUsers(new Map());
      setPendingChanges(new Map());
      setPendingOrgChange(null);
      setPendingPublicChange(null);
      setPendingGroupShares(new Map());
      setPendingGroupRemovals(new Set());
      setPendingGroupPermChanges(new Map());
      setMessage('');
      setSearchQuery('');
      setShowDiscardConfirm(false);
    }
  }, [open]);

  // Fetch organization users when modal opens, then auto-populate initialEmails
  useEffect(() => {
    if (open) {
      const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
          const response = await apiGet<OrganizationUser[]>('/tenant-management/members', {
            meta: { globalError: false, context: 'share-modal-users' }
          });
          const users = eligibleEmails
            ? response.data.filter((u) => eligibleEmails.some((e) => e.toLowerCase() === u.email.toLowerCase()))
            : response.data;
          setOrganizationUsers(users);

          if (initialEmails?.length) {
            const newMap = new Map<string, { user: OrganizationUser; permission: ResourcePermissionRole }>();
            for (const email of initialEmails) {
              const lowerEmail = email.toLowerCase();
              if (user_email && lowerEmail === user_email.toLowerCase()) continue;
              if (existingShareEmails.has(lowerEmail)) continue;
              const matchingUser = response.data.find((u) => u.email.toLowerCase() === lowerEmail);
              newMap.set(lowerEmail, {
                user: matchingUser || { user_id: 0, email, firstname: null, surname: null },
                permission: defaultPermission
              });
            }
            if (newMap.size > 0) setPendingNewUsers(newMap);
          }
        } catch (error) {
          console.error('Failed to fetch organization users:', error);
        } finally {
          setIsLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [open]);

  // Fetch groups when modal opens (only if onAddPeople is provided)
  useEffect(() => {
    if (open && canAddPeople && !individualsOnly) {
      const fetchGroups = async () => {
        try {
          const response = await apiGet<{ groups: ShareableGroup[] }>('/team-groups/', {
            meta: { globalError: false, context: 'share-modal-groups' }
          });
          setAvailableGroups(response.data.groups);
        } catch (error) {
          console.error('Failed to fetch groups:', error);
        }
      };
      fetchGroups();
    } else if (!open) {
      setAvailableGroups([]);
    }
  }, [open, canAddPeople, individualsOnly]);

  // Permission options
  const allPermissionOptions = [
    { label: 'View', value: ResourcePermissionRole.READ },
    { label: 'Comment', value: ResourcePermissionRole.COMMENT },
    { label: 'Data Entry', value: ResourcePermissionRole.DATA_ENTRY },
    { label: 'Edit', value: ResourcePermissionRole.WRITE },
    { label: 'Share', value: ResourcePermissionRole.SHARE },
    { label: 'Owner', value: ResourcePermissionRole.OWNER }
  ];

  const permissionOptions = acceptablePermissions
    ? allPermissionOptions.filter((option) => acceptablePermissions.includes(option.value))
    : allPermissionOptions;

  // Separate permission options for public sharing (e.g., restrict to READ only)
  const publicPermissionOptions = publicAcceptablePermissions
    ? allPermissionOptions.filter((option) => publicAcceptablePermissions.includes(option.value))
    : permissionOptions;

  // Existing share emails (lowercase)
  const existingShareEmails = useMemo(
    () => new Set(currentShares.map((s) => s.assignee.email.toLowerCase())),
    [currentShares]
  );

  // Organisation email set for detecting external users
  const orgEmailSet = useMemo(() => new Set(organizationUsers.map((u) => u.email.toLowerCase())), [organizationUsers]);

  const isExternalEmail = (email: string) =>
    maxExternalPermission !== undefined && !orgEmailSet.has(email.toLowerCase());

  // Filter group search results (exclude already-shared and pending groups)
  const existingGroupIds = useMemo(() => new Set(currentGroupShares.map((g) => g.group_id)), [currentGroupShares]);

  const filteredAvailableGroups = useMemo(
    () => (eligibleGroupIds ? availableGroups.filter((group) => eligibleGroupIds.includes(group.id)) : availableGroups),
    [availableGroups, eligibleGroupIds]
  );

  const shareOptions = useMemo(() => {
    const eligibleEmailSet = eligibleEmails ? new Set(eligibleEmails.map((email) => email.toLowerCase())) : null;
    const people: ComboboxOption[] = organizationUsers
      .filter((user) => {
        if (user_email && user.email.toLowerCase() === user_email.toLowerCase()) return false;
        if (existingShareEmails.has(user.email.toLowerCase())) return false;
        if (pendingNewUsers.has(user.email.toLowerCase())) return false;
        if (eligibleEmailSet && !eligibleEmailSet.has(user.email.toLowerCase())) return false;
        return true;
      })
      .map((user) => ({
        id: user.user_id,
        label: user.firstname && user.surname ? `${user.firstname} ${user.surname}` : user.email,
        value: user.email
      }));

    if (individualsOnly) return people;

    const groups: ComboboxOption[] = filteredAvailableGroups
      .filter((group) => !pendingGroupShares.has(group.id) && !existingGroupIds.has(group.id))
      .map((group) => ({
        id: group.id,
        label: group.name,
        value: group.id,
        hint: group.description?.trim() || 'Group'
      }));

    return [...people, ...groups];
  }, [
    organizationUsers,
    user_email,
    existingShareEmails,
    pendingNewUsers,
    eligibleEmails,
    individualsOnly,
    filteredAvailableGroups,
    pendingGroupShares,
    existingGroupIds
  ]);

  // Existing shares (excluding current user)
  const existingShares = useMemo(() => {
    return currentShares.filter(
      (share) => !user_email || share.assignee.email.toLowerCase() !== user_email.toLowerCase()
    );
  }, [currentShares, user_email]);

  const pendingPeopleEntries = useMemo(
    () =>
      Array.from(pendingNewUsers.entries()).filter(
        ([email]) =>
          !existingShareEmails.has(email) &&
          !currentShares.some((s) => !s.permission_id && s.assignee.email.toLowerCase() === email)
      ),
    [pendingNewUsers, existingShareEmails, currentShares]
  );

  const hasGroupRows = !individualsOnly && (pendingGroupShares.size > 0 || currentGroupShares.length > 0);
  const hasYouRow =
    currentUserPermission !== undefined ||
    isOwner ||
    !!currentShares.find((s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase());
  const hasPeopleRows = pendingPeopleEntries.length > 0 || existingShares.length > 0 || hasYouRow;

  // Get effective permission for an existing share (with pending changes applied).
  // For inherited-only users (no permission_id), check pending new users for upgrades.
  const getEffectivePermission = (share: ResourcePermission): ResourcePermissionRole | 'none' => {
    if (share.permission_id) {
      const change = pendingChanges.get(share.permission_id);
      if (change) {
        // If removing a direct grant but inherited floor exists, show inherited level
        if (change.type === 'remove') return share.inherited_permission ?? 'none';
        return change.newPermission;
      }
      return share.role;
    }
    // Inherited-only user — check if there's a pending upgrade
    const pendingUpgrade = pendingNewUsers.get(share.assignee.email.toLowerCase());
    if (pendingUpgrade) return pendingUpgrade.permission;
    return share.inherited_permission ?? share.role;
  };

  // Get effective org permission.
  // When both a direct grant and an inherited floor exist, the effective level
  // is the higher of the two (inherited floor can never be undercut).
  const getEffectiveOrgPermission = (): ResourcePermissionRole | 'none' => {
    if (pendingOrgChange) {
      if (pendingOrgChange.type === 'remove') return inheritedTenantPermission ?? 'none';
      return pendingOrgChange.permission;
    }
    if (tenantShare) {
      // If inherited floor is higher than the direct grant, show the inherited level
      if (inheritedTenantPermission && inheritedTenantPermission > tenantShare.role) {
        return inheritedTenantPermission;
      }
      return tenantShare.role;
    }
    return inheritedTenantPermission ?? 'none';
  };

  // Get effective public permission
  const getEffectivePublicPermission = (): ResourcePermissionRole | 'none' => {
    if (pendingPublicChange) {
      if (pendingPublicChange.type === 'remove') return 'none';
      return pendingPublicChange.permission;
    }
    return publicShare ? publicShare.role : 'none';
  };

  // Handle adding any email address
  const handleAddEmail = (rawEmail: string) => {
    const email = rawEmail.trim();

    if (!email) return;

    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address', { autoClose: 2000 });
      return;
    }

    if (user_email && email.toLowerCase() === user_email.toLowerCase()) {
      toast.error('You cannot share with yourself', { autoClose: 2000 });
      return;
    }

    if (existingShareEmails.has(email.toLowerCase())) {
      toast.error('This person already has access', { autoClose: 2000 });
      return;
    }

    if (pendingNewUsers.has(email.toLowerCase())) {
      toast.error('This person is already in the list', { autoClose: 2000 });
      return;
    }

    if (eligibleEmails && !eligibleEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      toast.error('This user does not have access to all referenced documents', { autoClose: 3000 });
      return;
    }

    const matchingUser = organizationUsers.find((u) => u.email.toLowerCase() === email.toLowerCase());
    const effectivePermission =
      !matchingUser && maxExternalPermission !== undefined
        ? (Math.min(defaultPermission, maxExternalPermission) as ResourcePermissionRole)
        : defaultPermission;

    setPendingNewUsers((prev) => {
      const newMap = new Map(prev);
      newMap.set(email.toLowerCase(), {
        user: matchingUser || { user_id: 0, email, firstname: null, surname: null },
        permission: effectivePermission
      });
      return newMap;
    });

    setSearchQuery('');
  };

  // Handle selecting a group from search results
  const handleSelectGroup = (group: ShareableGroup) => {
    setPendingGroupShares((prev) => {
      const newMap = new Map(prev);
      newMap.set(group.id, { group, permission: defaultPermission });
      return newMap;
    });
    setSearchQuery('');
  };

  const handlePickShareOption = (value: string) => {
    const group = individualsOnly ? undefined : availableGroups.find((item) => item.id === value);
    if (group) {
      handleSelectGroup(group);
      return;
    }
    handleAddEmail(value);
  };

  const handleUpdatePendingGroupPermission = (groupId: string, permission: ResourcePermissionRole | 'none') => {
    if (permission === 'none') {
      handleRemovePendingGroup(groupId);
      return;
    }
    setPendingGroupShares((prev) => {
      const newMap = new Map(prev);
      const entry = newMap.get(groupId);
      if (entry) {
        newMap.set(groupId, { ...entry, permission });
      }
      return newMap;
    });
  };

  // Handle removing a pending group share
  const handleRemovePendingGroup = (groupId: string) => {
    setPendingGroupShares((prev) => {
      const newMap = new Map(prev);
      newMap.delete(groupId);
      return newMap;
    });
  };

  const handleExistingGroupPermissionChange = (groupId: string, value: ResourcePermissionRole | 'none') => {
    if (value === 'none') {
      setPendingGroupRemovals((prev) => new Set(prev).add(groupId));
      setPendingGroupPermChanges((prev) => {
        const next = new Map(prev);
        next.delete(groupId);
        return next;
      });
      return;
    }
    setPendingGroupRemovals((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
    const currentGroup = currentGroupShares.find((g) => g.group_id === groupId);
    if (currentGroup && value !== currentGroup.permission) {
      setPendingGroupPermChanges((prev) => new Map(prev).set(groupId, value));
    } else {
      setPendingGroupPermChanges((prev) => {
        const next = new Map(prev);
        next.delete(groupId);
        return next;
      });
    }
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    openMoveAfterDiscardRef.current = false;
    if (hasAnyChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleMoveClick = () => {
    if (!onMove || isSubmitting) return;
    if (hasAnyChanges) {
      openMoveAfterDiscardRef.current = true;
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
    onMove();
  };

  // Handle removing from pending new users list
  const handleRemovePendingNew = (email: string) => {
    setPendingNewUsers((prev) => {
      const newMap = new Map(prev);
      newMap.delete(email.toLowerCase());
      return newMap;
    });
  };

  // Handle updating pending new user permission
  const handleUpdatePendingNewPermission = (email: string, permission: ResourcePermissionRole | 'none') => {
    if (permission === 'none') {
      handleRemovePendingNew(email);
    } else {
      const capped =
        isExternalEmail(email) && maxExternalPermission !== undefined
          ? (Math.min(permission, maxExternalPermission) as ResourcePermissionRole)
          : permission;
      setPendingNewUsers((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(email.toLowerCase());
        if (existing) {
          newMap.set(email.toLowerCase(), { ...existing, permission: capped });
        }
        return newMap;
      });
    }
  };

  // Handle existing share permission change (local only, batched)
  const handleExistingPermissionChange = (
    share: ResourcePermission,
    newPermission: ResourcePermissionRole | 'none'
  ) => {
    const inheritedFloor = share.inherited_permission;

    if (!share.permission_id) {
      // Inherited-only user — can only upgrade (creates a new direct grant)
      if (newPermission !== 'none' && newPermission !== inheritedFloor) {
        // Add to pending new users for the upgrade
        setPendingNewUsers((prev) => {
          const newMap = new Map(prev);
          newMap.set(share.assignee.email.toLowerCase(), {
            user: {
              user_id: 0,
              email: share.assignee.email,
              firstname: share.assignee.firstname || null,
              surname: share.assignee.surname || null
            },
            permission: newPermission
          });
          return newMap;
        });
      } else {
        // Back to inherited level — remove any pending upgrade
        setPendingNewUsers((prev) => {
          const newMap = new Map(prev);
          newMap.delete(share.assignee.email.toLowerCase());
          return newMap;
        });
      }
      return;
    }

    setPendingChanges((prev) => {
      const newMap = new Map(prev);

      // If setting back to original value, remove the pending change
      if (newPermission === share.role) {
        newMap.delete(share.permission_id!);
      } else if (newPermission === 'none' || (inheritedFloor && newPermission === inheritedFloor)) {
        // "No access" or selecting inherited level = remove direct grant
        // (inherited floor still provides access)
        if (inheritedFloor) {
          // Has inherited floor — removing direct grant just falls back to inherited
          newMap.set(share.permission_id!, { type: 'remove', permissionId: share.permission_id! });
        } else {
          // No inherited floor — full removal
          newMap.set(share.permission_id!, { type: 'remove', permissionId: share.permission_id! });
        }
      } else {
        newMap.set(share.permission_id!, {
          type: 'update',
          permissionId: share.permission_id!,
          newPermission: newPermission as ResourcePermissionRole
        });
      }

      return newMap;
    });
  };

  // Handle organisation permission change (local only, batched)
  const handleOrgPermissionChange = (permission: ResourcePermissionRole | 'none') => {
    const currentOrgPermission = tenantShare?.role;

    if (permission === 'none') {
      if (tenantShare) {
        // Mark for removal (revokes direct grant; inherited access remains if present)
        setPendingOrgChange({ type: 'remove' });
      } else {
        // No direct grant to remove — clear any pending change
        setPendingOrgChange(null);
      }
    } else if (tenantShare) {
      // Updating existing direct grant
      if (permission === currentOrgPermission) {
        // Back to original, clear pending change
        setPendingOrgChange(null);
      } else {
        setPendingOrgChange({ type: 'update', permission });
      }
    } else if (inheritedTenantPermission && permission === inheritedTenantPermission) {
      // Selected the same level as inherited — no-op, clear any pending change
      setPendingOrgChange(null);
    } else {
      // New direct share with org (upgrades beyond inherited level)
      setPendingOrgChange({ type: 'share', permission });
    }
  };

  // Handle public permission change (local only, batched)
  const handlePublicPermissionChange = (permission: ResourcePermissionRole | 'none') => {
    const currentPublicPermission = publicShare?.role;

    if (permission === 'none') {
      if (publicShare) {
        // Mark for removal
        setPendingPublicChange({ type: 'remove' });
      } else {
        // Already no access, clear any pending change
        setPendingPublicChange(null);
      }
    } else if (publicShare) {
      // Updating existing
      if (permission === currentPublicPermission) {
        // Back to original, clear pending change
        setPendingPublicChange(null);
      } else {
        setPendingPublicChange({ type: 'update', permission });
      }
    } else {
      // New public share
      setPendingPublicChange({ type: 'share', permission });
    }
  };

  // Apply all changes
  const handleApplyChanges = async () => {
    if (!hasAnyChanges) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Add new users and share with groups (grouped by permission level)
      const groupsByPermission = new Map<ResourcePermissionRole, string[]>();
      for (const [groupId, { permission }] of pendingGroupShares) {
        const ids = groupsByPermission.get(permission) || [];
        ids.push(groupId);
        groupsByPermission.set(permission, ids);
      }

      if (pendingNewUsers.size > 0 && onAddPeople) {
        const emailsByPermission = new Map<ResourcePermissionRole, string[]>();
        pendingNewUsers.forEach(({ permission }, email) => {
          const emails = emailsByPermission.get(permission) || [];
          emails.push(email);
          emailsByPermission.set(permission, emails);
        });

        for (const [permission, emails] of emailsByPermission) {
          const matchingGroupIds = groupsByPermission.get(permission);
          if (matchingGroupIds) groupsByPermission.delete(permission);
          await onAddPeople(emails, permission, message, matchingGroupIds);
        }
      }

      if (groupsByPermission.size > 0 && onAddPeople) {
        for (const [permission, groupIds] of groupsByPermission) {
          await onAddPeople([], permission, message, groupIds);
        }
      }

      // 2. Apply permission updates
      for (const [permissionId, change] of pendingChanges) {
        if (change.type === 'remove' && onRemovePerson) {
          await onRemovePerson(permissionId);
        } else if (change.type === 'update' && onUpdatePermission) {
          await onUpdatePermission(permissionId, change.newPermission);
        }
      }

      // 3. Apply org changes
      if (pendingOrgChange) {
        if (pendingOrgChange.type === 'share' && onShareWithOrganisation) {
          await onShareWithOrganisation(pendingOrgChange.permission);
        } else if (pendingOrgChange.type === 'update' && onUpdateOrganisationPermission) {
          await onUpdateOrganisationPermission(pendingOrgChange.permission);
        } else if (pendingOrgChange.type === 'remove' && onRemoveOrganisationShare) {
          await onRemoveOrganisationShare();
        }
      }

      // 4. Apply public changes
      if (pendingPublicChange) {
        if (pendingPublicChange.type === 'share' && onShareWithPublic) {
          await onShareWithPublic(pendingPublicChange.permission);
        } else if (pendingPublicChange.type === 'update' && onUpdatePublicPermission) {
          await onUpdatePublicPermission(pendingPublicChange.permission);
        } else if (pendingPublicChange.type === 'remove' && onRemovePublicShare) {
          await onRemovePublicShare();
        }
      }

      // 5. Remove group shares
      if (pendingGroupRemovals.size > 0 && onRemoveGroupShare) {
        for (const groupId of pendingGroupRemovals) {
          await onRemoveGroupShare(groupId);
        }
      }

      // 6. Update existing group permissions (revoke + re-share at new level)
      if (pendingGroupPermChanges.size > 0 && onRemoveGroupShare && onAddPeople) {
        for (const [groupId, perm] of pendingGroupPermChanges) {
          await onRemoveGroupShare(groupId);
          await onAddPeople([], perm, message, [groupId]);
        }
      }

      // Success message
      const actions: string[] = [];
      if (newUserCount > 0) {
        actions.push(`shared with ${newUserCount} ${newUserCount === 1 ? 'person' : 'people'}`);
      }
      if (pendingGroupCount > 0) {
        actions.push(`shared with ${pendingGroupCount} ${pendingGroupCount === 1 ? 'group' : 'groups'}`);
      }
      if (updateCount > 0) {
        actions.push(`updated ${updateCount} ${updateCount === 1 ? 'permission' : 'permissions'}`);
      }

      toast.success(actions.join(' and ').charAt(0).toUpperCase() + actions.join(' and ').slice(1), {
        autoClose: 2000
      });

      posthog?.capture('share_changes_applied', {
        shareType,
        newUsers: newUserCount,
        updates: updateCount
      });

      // Reset and close
      const addedEmails = Array.from(pendingNewUsers.keys());
      setPendingNewUsers(new Map());
      setPendingChanges(new Map());
      setPendingOrgChange(null);
      setPendingPublicChange(null);
      setPendingGroupShares(new Map());
      setPendingGroupRemovals(new Set());
      setPendingGroupPermChanges(new Map());
      setMessage('');
      onApplySuccess?.(addedEmails);
      onClose();
    } catch (error: any) {
      console.error('Failed to apply changes:', error);
      const message =
        error?.parsed?.description || error?.response?.data?.detail || 'Failed to save changes. Please try again.';
      toast.error(message, { autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonText = () => {
    if (isSubmitting) return 'Saving...';

    const shareCount = newUserCount + pendingGroupCount;
    if (shareCount > 0 && updateCount > 0) {
      return `Share with ${shareCount} & Update`;
    }
    if (shareCount > 0) {
      return `Share with ${shareCount}`;
    }
    if (updateCount > 0) {
      return 'Update Access';
    }
    return 'Done';
  };

  const shareListSections: Array<{ key: string; label?: string; content: React.ReactNode }> = [];
  if (onShareWithPublic) {
    shareListSections.push({
      key: 'public',
      label: 'Public',
      content: (
        <ShareItem
          name="Anyone"
          subtitle="All users can access"
          avatar={
            <div className="w-9 h-9 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <GlobeIcon size={18} weight="regular" />
            </div>
          }
          permission={getEffectivePublicPermission()}
          permissionOptions={publicPermissionOptions}
          onPermissionChange={handlePublicPermissionChange}
          onRemove={publicShare ? () => handlePublicPermissionChange('none') : undefined}
          onUndo={publicShare ? () => handlePublicPermissionChange(publicShare.role) : undefined}
        />
      )
    });
  }
  if (showOrganisationSharing) {
    shareListSections.push({
      key: 'organisation',
      label: 'Organisation',
      content: isReadOnlyOrgShare ? (
        <ShareItem
          name={`Members of ${organisationName}`}
          subtitle="You can only change organisation access for files you added."
          avatar={
            <div className="w-9 h-9 bg-gray-100 text-content-subtle rounded-lg flex items-center justify-center flex-shrink-0">
              <BuildingsIcon size={18} weight="regular" />
            </div>
          }
          permission={tenantShare?.role ?? inheritedTenantPermission ?? undefined}
          permissionOptions={allPermissionOptions}
          showNoAccess={false}
        />
      ) : (
        <ShareItem
          name={`Members of ${organisationName}`}
          subtitle={
            inheritedTenantPermission && (!tenantShare || inheritedTenantPermission > tenantShare.role)
              ? `${allPermissionOptions.find((o) => o.value === inheritedTenantPermission)?.label || 'Access'} via parent`
              : undefined
          }
          avatar={
            <div className="w-9 h-9 bg-gray-100 text-content-subtle rounded-lg flex items-center justify-center flex-shrink-0">
              <BuildingsIcon size={18} weight="regular" />
            </div>
          }
          permission={getEffectiveOrgPermission()}
          permissionOptions={allPermissionOptions.filter((o) => o.value === ResourcePermissionRole.READ)}
          showNoAccess={(!!tenantShare || pendingOrgChange?.type === 'share') && !inheritedTenantPermission}
          onPermissionChange={handleOrgPermissionChange}
          onRemove={
            !inheritedTenantPermission && (!!tenantShare || pendingOrgChange?.type === 'share')
              ? () => handleOrgPermissionChange('none')
              : undefined
          }
          onUndo={tenantShare ? () => handleOrgPermissionChange(tenantShare.role) : undefined}
        />
      )
    });
  }
  if (hasGroupRows) {
    shareListSections.push({
      key: 'groups',
      label: 'Groups',
      content: (
        <>
          {Array.from(pendingGroupShares.entries()).map(([groupId, { group, permission }]) => (
            <ShareItem
              key={`pending-group-${groupId}`}
              name={group.name}
              avatar={
                <div className="w-9 h-9 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <UsersThreeIcon size={18} weight="regular" />
                </div>
              }
              permission={permission}
              permissionOptions={permissionOptions}
              onPermissionChange={(p) => handleUpdatePendingGroupPermission(groupId, p)}
              onRemove={() => handleRemovePendingGroup(groupId)}
            />
          ))}
          {currentGroupShares.map((group) => {
            const effectivePerm = pendingGroupRemovals.has(group.group_id)
              ? ('none' as const)
              : (pendingGroupPermChanges.get(group.group_id) ?? group.permission);
            return (
              <ShareItem
                key={`existing-group-${group.group_id}`}
                name={group.group_name}
                avatar={
                  <div className="w-9 h-9 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <UsersThreeIcon size={18} weight="regular" />
                  </div>
                }
                permission={effectivePerm}
                permissionOptions={permissionOptions}
                onPermissionChange={(val) => handleExistingGroupPermissionChange(group.group_id, val)}
                onRemove={
                  onRemoveGroupShare ? () => handleExistingGroupPermissionChange(group.group_id, 'none') : undefined
                }
                onUndo={() => handleExistingGroupPermissionChange(group.group_id, group.permission)}
              />
            );
          })}
        </>
      )
    });
  }
  if (hasPeopleRows) {
    shareListSections.push({
      key: 'people',
      label: 'Users',
      content: (
        <>
          {pendingPeopleEntries.map(([email, { user, permission }]) => {
            const external = isExternalEmail(email);
            const willBeAddedAsGuest = user.user_id === 0;
            const effectiveOptions = external
              ? permissionOptions.filter((o) => o.value <= maxExternalPermission!)
              : permissionOptions;
            return (
              <ShareItem
                key={`pending-${email}`}
                name={user.firstname && user.surname ? `${user.firstname} ${user.surname}` : user.email}
                subtitle={
                  external
                    ? 'External user — view-only access'
                    : user.firstname && user.surname
                      ? user.email
                      : undefined
                }
                avatar={<UserAvatar firstname={user.firstname} surname={user.surname} email={user.email} />}
                warningMessage={
                  willBeAddedAsGuest ? "This user isn't in your organisation and will be added as a guest" : undefined
                }
                permission={permission}
                permissionOptions={effectiveOptions}
                onPermissionChange={(p) => handleUpdatePendingNewPermission(email, p)}
                onRemove={() => handleRemovePendingNew(email)}
              />
            );
          })}
          {existingShares.map((share) => {
            const inheritedFloor = share.inherited_permission;
            const inheritedLabel = inheritedFloor
              ? allPermissionOptions.find((o) => o.value === inheritedFloor)?.label
              : null;
            const sharePermOptions = inheritedFloor
              ? permissionOptions.filter((o) => o.value >= inheritedFloor)
              : permissionOptions;

            return (
              <ShareItem
                key={share.permission_id || `inherited-${share.assignee.email}`}
                name={
                  share.assignee.firstname && share.assignee.surname
                    ? `${share.assignee.firstname} ${share.assignee.surname}`
                    : share.assignee.email
                }
                subtitle={
                  inheritedFloor
                    ? `${inheritedLabel || 'Access'} via parent`
                    : share.assignee.firstname && share.assignee.surname
                      ? share.assignee.email
                      : undefined
                }
                avatar={
                  <UserAvatar
                    firstname={share.assignee.firstname}
                    surname={share.assignee.surname}
                    email={share.assignee.email}
                  />
                }
                permission={getEffectivePermission(share)}
                permissionOptions={sharePermOptions}
                onPermissionChange={(p) => handleExistingPermissionChange(share, p)}
                onRemove={!inheritedFloor ? () => handleExistingPermissionChange(share, 'none') : undefined}
                onUndo={share.permission_id ? () => handleExistingPermissionChange(share, share.role) : undefined}
              />
            );
          })}
          {(currentUserPermission !== undefined ||
            isOwner ||
            currentShares.find((s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase())) && (
            <ShareItem
              name={(() => {
                const userShare = currentShares.find(
                  (s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase()
                );
                const firstName = userShare?.assignee.firstname || user?.firstname;
                const surname = userShare?.assignee.surname || user?.surname;
                return firstName && surname ? `${firstName} ${surname} (You)` : `${user_email} (You)`;
              })()}
              avatar={
                <UserAvatar
                  firstname={
                    currentShares.find((s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase())
                      ?.assignee.firstname || user?.firstname
                  }
                  surname={
                    currentShares.find((s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase())
                      ?.assignee.surname || user?.surname
                  }
                  email={user_email}
                />
              }
              permission={
                currentUserPermission ??
                (isOwner
                  ? ResourcePermissionRole.OWNER
                  : (currentShares.find(
                      (s) => user_email && s.assignee.email.toLowerCase() === user_email.toLowerCase()
                    )?.role ?? ResourcePermissionRole.READ))
              }
              permissionOptions={permissionOptions}
              onPermissionChange={() => {}}
              showNoAccess={false}
              isCurrentUser={true}
            />
          )}
        </>
      )
    });
  }

  return (
    <>
      <Modal open={open} onClose={handleClose} title={title} className="w-full max-w-md min-h-[500px]">
        <div className="flex flex-1 flex-col gap-4 mt-2">
          <div className="flex-1 flex flex-col gap-4">
            {individualsOnly && (
              <Banner variant="info" icon={InfoIcon}>
                Documents can be shared with individuals only.
                {locationHint ? ` ${locationHint}` : null}
                {canMove && onMove && (
                  <>
                    {' '}
                    To add a file to a group or the Organisation Folder, use{' '}
                    <button
                      type="button"
                      className="font-medium underline underline-offset-2 hover:text-blue-900"
                      disabled={isSubmitting}
                      onClick={handleMoveClick}
                    >
                      Move
                    </button>
                    .
                  </>
                )}
              </Banner>
            )}
            {mentionPrompt && mentionPrompt.mentionsNeedingAccess.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <WarningCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800">
                    {mentionPrompt.mentionsNeedingAccess.length === 1
                      ? "The person you mentioned doesn't currently have access."
                      : `${mentionPrompt.mentionsNeedingAccess.length} people you mentioned don't currently have access.`}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {mentionPrompt.mentionsNeedingAccess.map((m, i) => (
                      <span
                        key={m.email || i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-900 rounded text-xs font-medium"
                      >
                        {m.displayName || m.email}
                      </span>
                    ))}
                  </div>
                  {mentionPrompt.recommendedPermissionHint && (
                    <p className="text-xs text-amber-700 mt-1.5">{mentionPrompt.recommendedPermissionHint}</p>
                  )}
                </div>
              </div>
            )}

            {/* Email/Search Input - only shown when individual sharing is allowed */}
            {onAddPeople && (
              <div>
                <label htmlFor={searchInputId} className="sr-only">
                  Add people
                </label>
                <Combobox
                  variant="field"
                  fullWidth
                  id={searchInputId}
                  options={shareOptions}
                  value={searchQuery}
                  onSearch={setSearchQuery}
                  onSelect={handlePickShareOption}
                  allowCreate={!eligibleEmails}
                  validateValue={eligibleEmails ? undefined : isValidEmail}
                  placeholder={
                    isLoadingUsers
                      ? 'Loading users...'
                      : individualsOnly
                        ? 'Search people by name or email'
                        : 'Search people or groups by name or email'
                  }
                  emptyText={
                    eligibleEmails
                      ? isValidEmail(searchQuery.trim())
                        ? 'This user does not have access to all referenced documents and cannot be added.'
                        : 'No matching users. Only users with access to all referenced documents can be added.'
                      : 'No matching users found'
                  }
                  disabled={isLoadingUsers}
                />
              </div>
            )}

            {/* Info text */}
            {onAddPeople && !individualsOnly && (
              <p className="text-xs text-content-subtle">Only those invited can access this resource.</p>
            )}

            {/* Restriction warning */}
            {shareRestrictionReason && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                {shareRestrictionReason}
              </div>
            )}

            {/* People List — grouped with borders: Public, Organisation, Groups, People */}
            <div className="border-t border-gray-100 max-h-[320px] overflow-y-auto">
              {shareListSections.map((section, index) => (
                <div key={section.key} className={index > 0 ? 'border-t border-border-subtle mt-3 pt-3' : 'pt-3'}>
                  {section.label ? (
                    <div className="text-xs mb-1 font-medium text-content-tertiary uppercase tracking-wide">
                      {section.label}
                    </div>
                  ) : null}
                  {section.content}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-2 justify-end pt-3 border-t border-gray-100 flex-shrink-0">
            {/* Message Input (when adding new users) */}
            {pendingNewUsers.size > 0 && (
              <div>
                <label htmlFor={messageInputId} className="block text-sm font-medium text-content-default mb-1">
                  Message (optional)
                </label>
                <Textarea
                  id={messageInputId}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a message..."
                  rows={2}
                />
              </div>
            )}

            {organisationShareWarning && pendingOrgChange?.type === 'share' && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex-1">
                <WarningCircleIcon size={16} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                {organisationShareWarning}
              </div>
            )}
            <Button
              onClick={handleApplyChanges}
              variant="primary"
              size="md"
              disabled={isSubmitting}
              className="min-w-[120px]"
            >
              {getButtonText()}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Discard changes confirmation */}
      <ConfirmModal
        open={showDiscardConfirm}
        onClose={() => {
          openMoveAfterDiscardRef.current = false;
          setShowDiscardConfirm(false);
        }}
        onConfirm={() => {
          if (isSubmitting) {
            openMoveAfterDiscardRef.current = false;
            setShowDiscardConfirm(false);
            return;
          }
          const openMove = openMoveAfterDiscardRef.current;
          openMoveAfterDiscardRef.current = false;
          setShowDiscardConfirm(false);
          onClose();
          if (openMove) onMove?.();
        }}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        variant="danger"
      />
    </>
  );
};

export default ShareModal;
